import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";

type OrgResponse = { domain?: string | null };

type GroupRow = {
  id: string;
  name?: string;
  description?: string | null;
  memberCount?: number;
};

type GroupsResponse = { groups?: GroupRow[] };

export function SettingsGroupsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<GroupRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const orgRes = await apiGet<OrgResponse>("/api/me/organization");
      const domain = orgRes.data?.domain?.trim();
      if (!domain) {
        if (!cancelled) {
          setError("Organization domain required.");
          setGroups([]);
          setLoading(false);
        }
        return;
      }
      const res = await apiGet<GroupsResponse>(
        `/api/groups?accountDomain=${encodeURIComponent(domain)}`,
      );
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load groups.");
        setGroups([]);
      } else {
        setError(null);
        setGroups(res.data?.groups ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Groups</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Profile groups from <code className="text-sm">/api/groups</code>. Manage members in Directory.
        </p>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading groups…</p> : null}
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {!loading && !error ? (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-slate-950/40">
          {groups.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-slate-500">No groups yet.</li>
          ) : (
            groups.map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{g.name || g.id}</p>
                  {g.description ? (
                    <p className="text-sm text-slate-600 dark:text-slate-400">{g.description}</p>
                  ) : null}
                </div>
                <span className="text-xs text-slate-500">
                  {typeof g.memberCount === "number" ? `${g.memberCount} members` : ""}
                </span>
              </li>
            ))
          )}
        </ul>
      ) : null}

      <Link to="/settings" className="text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-300">
        ← Settings overview
      </Link>
    </div>
  );
}
